import React, {FC} from 'react';

import download from 'assets/v3/guide/image/download.webp';
import engineConfig from 'assets/v3/guide/image/engine-config.webp';
import refine from 'assets/v3/guide/image/refine.webp';
import refineDone from 'assets/v3/guide/image/refine-done.webp';
import result from 'assets/v3/guide/image/result.webp';
import translating from 'assets/v3/guide/image/translating.webp';
import uploadEcommerce1 from 'assets/v3/guide/image/upload-ecommerce-1.webp';
import uploadEcommerce2 from 'assets/v3/guide/image/upload-ecommerce-2.webp';
import uploadLink from 'assets/v3/guide/image/upload-link.webp';
import uploadLocal from 'assets/v3/guide/image/upload-local.webp';

/** 使用指南「图片翻译」tab 英文正文（与 zh.tsx 元素结构 1:1） */
const EnContent: FC = () => {
  return (
    <>
      <h2>1. Before You Upload</h2>
      <p>Before uploading, choose the version, source language and target language as needed.</p>
      <img
        alt="Translation settings: version, source and target languages"
        decoding="async"
        loading="lazy"
        src={engineConfig}
      />
      <p>
        <strong>Source language</strong>: choose the language of the text in the original image.
      </p>
      <p>
        <strong>Target language</strong>: the language you want to translate into.
      </p>

      <h3>Which translation engine should I choose?</h3>
      <div className="v3-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Engine</th>
              <th>Strengths</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Alibaba Cloud / Baidu</td>
              <td>
                More idiomatic Chinese output, best for translating foreign languages into Chinese; slightly weaker when
                translating Chinese into foreign languages, but cheaper.
              </td>
            </tr>
            <tr>
              <td>Google</td>
              <td>Better for foreign-language translation, with broad language coverage.</td>
            </tr>
            <tr>
              <td>Papago</td>
              <td>From the Korean cloud provider Naver — the most idiomatic choice for translating into Korean.</td>
            </tr>
            <tr>
              <td>DeepL</td>
              <td>
                Better for foreign-language translation with broad coverage (Japanese, English, German, Russian, etc.),
                and excellent with slang, dialects and classical phrasing.
              </td>
            </tr>
            <tr>
              <td>ChatGPT</td>
              <td>The leading natural language model today — accurate, idiomatic text translation.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>2. Uploading Images</h2>
      <h3>Upload local images</h3>
      <p>Click the upload button, browse your local files and select the product images you need.</p>
      <img alt="Upload local files" decoding="async" loading="lazy" src={uploadLocal} />

      <h3>Enter image links</h3>
      <p>Switch to the image-link tab to batch-import the image URLs you want to translate.</p>
      <img alt="Enter direct links" decoding="async" loading="lazy" src={uploadLink} />

      <h3>Paste an e-commerce link</h3>
      <p>Paste a product link to collect its images automatically, then select the ones you want to translate.</p>
      <img alt="Paste an e-commerce link" decoding="async" loading="lazy" src={uploadEcommerce1} />
      <img alt="Collected product visuals" decoding="async" loading="lazy" src={uploadEcommerce2} />

      <h2>3. Image Translation</h2>
      <p>After uploading, click the 【Translate Now】button and AI starts translating automatically.</p>
      <img alt="Start translating" decoding="async" loading="lazy" src={translating} />
      <p>
        AI image translation takes about 2–3 seconds. When it finishes, compare the original and translated images side
        by side. If you are satisfied, click 【Download All】 or 【Copy Translated Image Link】 as needed; if not, or if
        you want to redo the layout, click 【Retouch】 to open the retouch tool and adjust the image.
      </p>
      <img alt="Original and translated comparison" decoding="async" loading="lazy" src={result} />

      <h2>4. Image Retouch</h2>
      <p>Click 【Retouch】 to open the retouch workbench, then click the text you want to edit.</p>
      <ul>
        <li>
          <strong>Text editing area</strong>: edit the original text and re-translate it; adjust text style, angle,
          stroke and more.
        </li>
        <li>
          <strong>Repair tools</strong>: add text for translation, or use the smart eraser to remove or fix blemishes.
        </li>
      </ul>
      <img alt="Retouch workbench" decoding="async" loading="lazy" src={refine} />
      <p>When you are done, click 【Finish Editing】 to save the changes and exit the retouch tool.</p>
      <img alt="Finish retouching" decoding="async" loading="lazy" src={refineDone} />

      <h2>5. Download</h2>
      <p>
        Click 【Download All】 or 【Copy Image Link】. Retouched images automatically replace the non-retouched ones
        when downloading or exporting.
      </p>
      <img alt="Download translated results" decoding="async" loading="lazy" src={download} />
    </>
  );
};

export default EnContent;
